import React, { useEffect, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { composePlaceLabel } from '../utils/composePlaceLabel';
import { typedPlaceWarning } from '../utils/typedPlaceDistance';
import './AddressAutocomplete.css';

let mapsInitPromise = null;

function loadGooglePlaces() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return Promise.reject(
      new Error('Google Maps API key is not configured (REACT_APP_GOOGLE_MAPS_API_KEY).')
    );
  }

  if (!mapsInitPromise) {
    setOptions({ key: apiKey, v: 'weekly' });
    mapsInitPromise = importLibrary('places');
  }

  return mapsInitPromise;
}

const PLACES_NEW_API_HINT =
  'Enable **Places API (New)** on your Google Cloud project and add it to this browser API key ' +
  '(API restrictions must list “Places API (New)”, not only “Places API”). ' +
  'Changes can take 5–15 minutes; then hard-refresh.';

function extractGmpErrorMessage(event) {
  if (!event) return '';
  const nested = event.error ?? event.detail?.error ?? event.detail;
  if (typeof nested === 'string') return nested;
  if (nested?.message) return nested.message;
  if (nested?.status) return String(nested.status);
  if (event.message) return event.message;
  return '';
}

function isNewPlacesApiBlocked(message) {
  const lower = (message || '').toLowerCase();
  return (
    lower.includes('autocompleteplaces') ||
    lower.includes('places.googleapis.com') ||
    lower.includes('are blocked') ||
    lower.includes('403')
  );
}

function mapGoogleMapsError(errOrMessage) {
  const msg =
    typeof errOrMessage === 'string'
      ? errOrMessage
      : errOrMessage?.message || String(errOrMessage ?? '');
  const lower = msg.toLowerCase();

  if (isNewPlacesApiBlocked(msg)) {
    return `Places API (New) is not authorized for this key (403 on AutocompletePlaces). ${PLACES_NEW_API_HINT}`;
  }

  if (
    msg.includes('ApiTargetBlockedMapError') ||
    msg.includes('ApiNotActivatedMapError') ||
    lower.includes('not authorized') ||
    lower.includes('blocked') ||
    lower.includes('permission_denied')
  ) {
    return `Google Maps blocked address search. ${PLACES_NEW_API_HINT}`;
  }

  if (msg && msg !== '[object Event]' && msg !== '[object Object]') {
    return `${msg} ${PLACES_NEW_API_HINT}`;
  }

  return PLACES_NEW_API_HINT;
}

function placeIdFromLegacy(place) {
  return place?.place_id || '';
}

/** Match `.input-field` styling and force light theme (avoids system dark-mode black bars). */
function applyPlaceAutocompleteStyles(widget) {
  widget.style.setProperty('color-scheme', 'light');
  widget.style.setProperty('background-color', '#fafaf9');
  widget.style.setProperty('border', '1px solid #e7e5e4');
  widget.style.setProperty('border-radius', '0.75rem');
  widget.style.setProperty('width', '100%');
  widget.style.setProperty('max-width', '100%');
  widget.style.setProperty('box-sizing', 'border-box');
}

/**
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {{ placeId: string, label: string, lat?: number, lng?: number } | null} props.value
 * @param {(place: { placeId: string, label: string, lat?: number, lng?: number } | null) => void} props.onChange
 * @param {string} [props.placeholder]
 */
const AddressAutocomplete = ({ id, label, value, onChange, placeholder, biasCenter, biasRadiusKm, disabled }) => {
  const hostRef = useRef(null);
  const widgetRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const legacyFallbackAttemptedRef = useRef(false);
  const valueRef = useRef(value);
  const disabledRef = useRef(disabled);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [usingLegacy, setUsingLegacy] = useState(false);
  const typedTextRef = useRef('');
  const [distanceWarning, setDistanceWarning] = useState(null);
  onChangeRef.current = onChange;
  valueRef.current = value;
  disabledRef.current = disabled;

  useEffect(() => {
    const w = widgetRef.current;
    if (!w) return;

    if (w.kind === 'legacy' && w.input) {
      const next = value?.label ?? '';
      if (w.input.value !== next) {
        w.input.value = next;
      }
      return;
    }

    if (w.kind === 'new' && value?.label != null) {
      if (value.label && w.element.value !== value.label) {
        w.element.value = value.label;
      }
      if (!value.placeId) {
        w.element.value = '';
      }
    }
  }, [value?.label, value?.placeId]);

  useEffect(() => {
    if (!value?.placeId) setDistanceWarning(null);
  }, [value?.placeId]);

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    legacyFallbackAttemptedRef.current = false;

    const emitPlace = (placeId, labelText, lat, lng) => {
      if (!placeId) {
        onChangeRef.current(null);
        return;
      }
      setLoadError(null);
      setDistanceWarning(typedPlaceWarning(typedTextRef.current, { label: labelText, lat, lng }));
      onChangeRef.current({
        placeId,
        label: labelText,
        ...(lat != null && lng != null ? { lat, lng } : {}),
      });
    };

    const mountLegacy = (placesLib) => {
      if (!hostRef.current || !placesLib?.Autocomplete) {
        setLoadError(mapGoogleMapsError('Legacy Places Autocomplete is not available.'));
        return;
      }

      hostRef.current.innerHTML = '';
      const input = document.createElement('input');
      input.type = 'text';
      input.id = id;
      input.name = id;
      input.className = 'input-field w-full';
      input.placeholder = placeholder ?? 'Search address in Kenya or Tanzania';
      input.autoComplete = 'off';
      if (disabledRef.current) {
        input.disabled = true;
      }
      const initialLabel = valueRef.current?.label;
      if (initialLabel) {
        input.value = initialLabel;
      }

      const autocomplete = new placesLib.Autocomplete(input, {
        componentRestrictions: { country: ['ke', 'tz'] },
        fields: ['place_id', 'formatted_address', 'geometry', 'name'],
      });

      if (biasCenter?.lat != null && biasCenter?.lng != null && biasRadiusKm) {
        const radiusM = biasRadiusKm * 1000;
        const latDelta = radiusM / 111320;
        const lngDelta = radiusM / (111320 * Math.cos((biasCenter.lat * Math.PI) / 180));
        const bounds = new window.google.maps.LatLngBounds(
          { lat: biasCenter.lat - latDelta, lng: biasCenter.lng - lngDelta },
          { lat: biasCenter.lat + latDelta, lng: biasCenter.lng + lngDelta },
        );
        autocomplete.setBounds(bounds);
        autocomplete.setOptions({ strictBounds: false });
      }

      const onPlaceChanged = () => {
        const place = autocomplete.getPlace();
        const placeId = placeIdFromLegacy(place);
        if (!placeId) return;

        const labelText =
          composePlaceLabel(place.name, place.formatted_address) ||
          input.value ||
          '';
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();

        emitPlace(placeId, labelText, lat, lng);
      };

      autocomplete.addListener('place_changed', onPlaceChanged);
      input.addEventListener('input', () => {
        typedTextRef.current = input.value;
        if (!input.value.trim()) {
          onChangeRef.current(null);
        }
      });
      hostRef.current.appendChild(input);

      widgetRef.current = { kind: 'legacy', input, autocomplete, onPlaceChanged };
      setUsingLegacy(true);
      setLoadError(null);
      setReady(true);
    };

    const mountNew = (placesLib) => {
      const widgetOptions = {
        includedRegionCodes: ['ke', 'tz'],
      };
      if (biasCenter?.lat != null && biasCenter?.lng != null && biasRadiusKm) {
        widgetOptions.locationBias = {
          center: { lat: biasCenter.lat, lng: biasCenter.lng },
          radius: biasRadiusKm * 1000,
        };
      }

      const widget = new placesLib.PlaceAutocompleteElement(widgetOptions);

      widget.id = id;
      widget.placeholder = placeholder ?? 'Search address in Kenya or Tanzania';
      widget.className = 'w-full max-w-full';
      if (disabledRef.current) {
        widget.disabled = true;
      }
      applyPlaceAutocompleteStyles(widget);

      // Typing happens in the widget's shadow-DOM input; the composed event reaches the host.
      const handleTyping = (event) => {
        const inner = event.composedPath?.()[0];
        if (typeof inner?.value === 'string') typedTextRef.current = inner.value;
      };

      const handleSelect = async (event) => {
        try {
          const placePrediction = event.placePrediction;
          if (!placePrediction) return;

          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: ['id', 'formattedAddress', 'location', 'displayName'],
          });

          const placeId = place.id;
          if (!placeId) {
            onChangeRef.current(null);
            return;
          }

          const labelText =
            composePlaceLabel(place.displayName, place.formattedAddress) ||
            placePrediction.text?.text ||
            '';

          const lat = place.location?.lat();
          const lng = place.location?.lng();

          emitPlace(placeId, labelText, lat, lng);
        } catch (err) {
          console.error('Place select error', err);
          onChangeRef.current(null);
        }
      };

      const handleError = (event) => {
        const detail = extractGmpErrorMessage(event);
        const blocked = isNewPlacesApiBlocked(detail);

        if (blocked && !legacyFallbackAttemptedRef.current && placesLib.Autocomplete) {
          legacyFallbackAttemptedRef.current = true;
          console.warn(
            'Places API (New) blocked; falling back to legacy Autocomplete. Enable Places API (New) on your key.'
          );
          widget.removeEventListener('gmp-select', handleSelect);
          widget.removeEventListener('gmp-error', handleError);
          widget.removeEventListener('input', handleTyping);
          widget.remove();
          widgetRef.current = null;
          if (!cancelled) {
            mountLegacy(placesLib);
          }
          return;
        }

        console.warn('PlaceAutocomplete gmp-error:', detail || '(request denied)');
        if (!cancelled) {
          setLoadError(mapGoogleMapsError(detail));
        }
      };

      widget.addEventListener('gmp-select', handleSelect);
      widget.addEventListener('gmp-error', handleError);
      widget.addEventListener('input', handleTyping);

      hostRef.current.innerHTML = '';
      hostRef.current.appendChild(widget);
      widgetRef.current = { kind: 'new', element: widget, handleSelect, handleError, handleTyping };
      setUsingLegacy(false);
      setReady(true);
      setLoadError(null);
    };

    (async () => {
      try {
        await loadGooglePlaces();
        if (cancelled || !hostRef.current) return;

        const placesLib = window.google?.maps?.places;
        if (!placesLib?.PlaceAutocompleteElement) {
          if (placesLib?.Autocomplete) {
            mountLegacy(placesLib);
            return;
          }
          throw new Error('Google Places library did not load.');
        }

        mountNew(placesLib);
      } catch (err) {
        if (!cancelled) {
          setLoadError(mapGoogleMapsError(err));
        }
      }
    })();

    cleanup = () => {
      cancelled = true;
      const w = widgetRef.current;
      if (!w) return;

      if (w.kind === 'new') {
        w.element.removeEventListener('gmp-select', w.handleSelect);
        w.element.removeEventListener('gmp-error', w.handleError);
        w.element.removeEventListener('input', w.handleTyping);
        w.element.remove();
      } else if (w.kind === 'legacy') {
        window.google?.maps?.event?.clearInstanceListeners?.(w.autocomplete);
        w.input.remove();
      }
      widgetRef.current = null;
    };

    return cleanup;
  }, [id, placeholder, retryKey, biasCenter?.lat, biasCenter?.lng, biasRadiusKm]);

  useEffect(() => {
    const w = widgetRef.current;
    if (!w) return;
    if (w.kind === 'legacy' && w.input) {
      w.input.disabled = !!disabled;
    } else if (w.kind === 'new') {
      w.element.disabled = !!disabled;
    }
  }, [disabled]);

  const hostClassName = usingLegacy
    ? 'block w-full min-w-0 max-w-full'
    : 'address-autocomplete-host w-full min-w-0 max-w-full';

  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2"
      >
        {label}
      </label>
      <div ref={hostRef} className={hostClassName} aria-busy={!ready && !loadError} />
      {distanceWarning && (
        <p role="status" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
          {distanceWarning}
        </p>
      )}
      {usingLegacy && ready && !loadError && (
        <p className="text-xs text-stone-500 mt-1">
          Using compatibility address search. For the latest widget, enable Places API (New) on
          your Google Cloud key.
        </p>
      )}
      {loadError && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {loadError.replace(/\*\*/g, '')}
          </p>
          <button
            type="button"
            className="text-xs font-semibold text-[#2D1E17] underline hover:no-underline"
            onClick={() => {
              setLoadError(null);
              setReady(false);
              setUsingLegacy(false);
              setRetryKey((k) => k + 1);
            }}
          >
            Retry address search
          </button>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
